import React, { useState, useRef, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { ModeSelector, type ECOMode } from './ModeSelector';
import { TemplateSelector } from './TemplateSelector';
import { FileUploader } from './FileUploader';
import { ChatBubble } from './ChatBubble';
import { ECOSidebar } from './ECOSidebar';
import { supabase } from '@/integrations/supabase/client';
import { useFeatureModel } from '@/contexts/FeatureModelContext';
import { useStreamingECO } from '@/hooks/useStreamingECO';
import { ecoTemplates } from '@/data/ecoTemplates';
import type { ECOMessage, UploadedFile, ECOResponse } from './types';

const ECOAssistantView: React.FC = () => {
  const [messages, setMessages] = useState<ECOMessage[]>([]);
  const [content, setContent] = useState('');
  const [mode, setMode] = useState<ECOMode>('entire');
  const [selectedTemplate, setSelectedTemplate] = useState('entire_eco');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { trackTokenUsage, hasReachedUsageLimit } = useFeatureModel();
  const { enhanceContentStreaming, cancelStreaming, isStreaming, streamingContent } = useStreamingECO();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-scroll during streaming with throttle
  useEffect(() => {
    if (isStreaming && streamingContent) {
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isStreaming, streamingContent]);

  // Handle mode changes
  const handleModeChange = (newMode: ECOMode) => {
    setMode(newMode);
    if (newMode === 'entire') {
      setSelectedTemplate('entire_eco');
    } else {
      setSelectedTemplate('pro_tip'); // Default to first individual template
    }
  };

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error('Please enter some content to enhance');
      return;
    }

    if (hasReachedUsageLimit('ecoAssistant')) {
      toast.error('Daily usage limit reached. Please try again tomorrow.');
      return;
    }

    setIsLoading(true);

    // Add user message
    const userMessage: ECOMessage = {
      id: Date.now().toString(),
      type: 'user',
      content,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    // Create placeholder assistant message for streaming
    const assistantMessageId = (Date.now() + 1).toString();
    const assistantMessage: ECOMessage = {
      id: assistantMessageId,
      type: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true
    };

    setMessages(prev => [...prev, assistantMessage]);
    setStreamingMessageId(assistantMessageId);

    try {
      // Prepare context from uploaded files
      const contextText = uploadedFiles
        .map(file => `${file.name}:\n${file.content}`)
        .join('\n\n');

      await enhanceContentStreaming(
        {
          content,
          templateId: selectedTemplate,
          contextFiles: contextText || undefined,
          mode
        },
        // On streaming update
        (updatedMessage) => {
          setMessages(prev => prev.map(msg => 
            msg.id === assistantMessageId 
              ? { ...msg, ...updatedMessage }
              : msg
          ));
        },
        // On completion
        (finalMessage) => {
          setMessages(prev => prev.map(msg => 
            msg.id === assistantMessageId 
              ? { ...finalMessage, id: assistantMessageId, isStreaming: false }
              : msg
          ));
          setStreamingMessageId(null);
          toast.success('Content enhanced successfully!');
        }
      );

      // Clear input
      setContent('');

    } catch (error) {
      console.error('Error enhancing content:', error);
      // Remove the placeholder message on error
      setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
      setStreamingMessageId(null);
      toast.error('Failed to enhance content. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isLoading && !isStreaming) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleCancel = () => {
    cancelStreaming();
    if (streamingMessageId) {
      setMessages(prev => prev.filter(msg => msg.id !== streamingMessageId));
      setStreamingMessageId(null);
    }
    setIsLoading(false);
  };

  const clearChat = () => {
    setMessages([]);
    setContent('');
    setUploadedFiles([]);
    toast.success('Chat cleared');
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <ECOSidebar
        mode={mode}
        selectedTemplate={selectedTemplate}
        uploadedFiles={uploadedFiles}
        onModeChange={handleModeChange}
        onTemplateSelect={setSelectedTemplate}
        onFilesChange={setUploadedFiles}
        onRemoveFile={removeFile}
        onClearChat={clearChat}
        messagesCount={messages.length}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="border-b border-border/60 bg-card/30 backdrop-blur-sm px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-purple-400 rounded-lg flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold">ECO Assistant</h1>
              <p className="text-sm text-muted-foreground">
                {mode === 'entire' 
                  ? 'Complete content enhancement with all 7 blocks'
                  : `Individual enhancement: ${ecoTemplates.find(t => t.id === selectedTemplate)?.label || 'Selected style'}`
                }
              </p>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full px-6">
                <div className="text-center max-w-md">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-600/20 to-purple-400/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="h-8 w-8 text-purple-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Ready to enhance your content</h3>
                  <p className="text-muted-foreground">
                    {mode === 'entire' 
                      ? 'Paste your content below and get a complete ECO enhancement with all 7 blocks!'
                      : 'Paste your content below, select an enhancement style, and let AI work its magic!'
                    }
                  </p>
                </div>
              </div>
            ) : (
              <div className="pb-4">
                {messages.map((message) => (
                  <ChatBubble 
                    key={message.id} 
                    message={message} 
                    isStreaming={message.id === streamingMessageId && isStreaming}
                    streamingContent={message.id === streamingMessageId ? streamingContent : undefined}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Input Area */}
        <div className="border-t border-border/60 bg-card/30 backdrop-blur-sm p-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-3">
              <div className="flex-1">
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={mode === 'entire' 
                    ? "Paste your content here for complete ECO enhancement..."
                    : "Paste your content here to enhance it..."
                  }
                  className="min-h-[80px] resize-none border-border/60 focus:border-purple-400 transition-colors"
                  disabled={isLoading || isStreaming}
                />
                {uploadedFiles.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {uploadedFiles.length} file(s) attached as context
                  </p>
                )}
              </div>
              
              <div className="flex flex-col gap-2">
                {isStreaming ? (
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                    className="px-4 py-2 h-auto"
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={!content.trim() || isLoading}
                    className="px-4 py-2 h-auto bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600"
                  >
                    {isLoading ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ECOAssistantView;